import React, { useEffect, useState } from 'react';
import {
  Settings as SettingsIcon, Save, Loader2, AlertTriangle, RefreshCw,
  Server, Cpu, Globe, Clock, Shield, DollarSign, Sparkles,
  Info, ChevronRight, Activity, Database,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Spinner } from '../components/Dialog';
import { OperatorStat } from '../components/OperatorStat';
import { adminFetch } from '../adminApi';
import { cn } from '../lib/utils';

/**
 * SettingsPage — runtime-tunable system config.
 *
 * Phase-23: surfaces /admin/settings. Right now the only persisted knob is
 * the yearly discount (saved in `system_settings`); the rest of the panel
 * shows what *can* be tuned via env vars.
 *
 * Future tunables will land here as we add operator-level config to the DB.
 */
export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draftPct, setDraftPct] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  async function load() {
    setLoading(true); setError(null);
    try {
      const s = await adminFetch('/admin/settings');
      setSettings(s);
      setDraftPct(String(s.yearly_discount_pct ?? ''));
    } catch (e) {
      setError(e.message || 'failed_to_load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    const pct = parseFloat(draftPct);
    if (isNaN(pct) || pct < 0 || pct > 50) {
      alert('Yearly discount must be between 0 and 50%.');
      return;
    }
    setBusy(true);
    try {
      await adminFetch('/admin/settings', {
        method: 'PATCH',
        body: { yearly_discount_bps: Math.round(pct * 100) },
      });
      await load();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title="System settings"
        description="Runtime-tunable configuration. Changes apply across all instances without a restart."
        actions={
          <Button variant="secondary" size="sm" onClick={load}>
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        }
      />

      {/* Top stats — quick at-a-glance numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <OperatorStat
          icon={Sparkles}
          label="Yearly discount"
          value={loading ? null : `${settings?.yearly_discount_pct ?? '—'}%`}
          hint={loading ? 'Loading…' : (settings?.source === 'db' ? 'Overridden in DB' : 'Default (env)')}
          tone="brand"
          loading={loading}
        />
        <OperatorStat
          icon={Clock}
          label="TLS timeout"
          value={loading ? null : `${settings?.tls_timeout_seconds ?? '—'}s`}
          hint={loading ? 'Loading…' : 'Per-request'}
          tone="neutral"
          loading={loading}
        />
        <OperatorStat
          icon={Server}
          label="Datames max timeout"
          value={loading ? null : `${Math.round((settings?.datames_max_timeout_ms ?? 0) / 1000)}s`}
          hint={loading ? 'Loading…' : 'Stealth solve budget'}
          tone="neutral"
          loading={loading}
        />
        <OperatorStat
          icon={Cpu}
          label="TLS identities"
          value={loading ? null : (settings?.client_identifiers?.length ?? '—')}
          hint={loading ? 'Loading…' : 'Browser fingerprints'}
          tone="neutral"
          loading={loading}
        />
      </div>

      {error && (
        <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      {/* Yearly discount editor */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-strong" />
            <div>
              <Card.Title>Yearly discount</Card.Title>
              <Card.Subtitle>
                Applied automatically when a customer picks yearly billing.
                Persisted to <code className="font-mono text-fg">public.system_settings</code>.
              </Card.Subtitle>
            </div>
          </div>
          {savedFlash && (
            <Badge variant="success"><Dot variant="success" /> Saved</Badge>
          )}
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end max-w-md">
            <div>
              <label className="text-2xs uppercase tracking-wider text-fg-muted font-medium block mb-1">
                Discount (%)
              </label>
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  min={0}
                  max={50}
                  step={0.5}
                  value={draftPct}
                  onChange={(e) => setDraftPct(e.target.value)}
                  className="font-mono text-right"
                  disabled={loading}
                />
                <span className="text-sm text-fg-muted">%</span>
              </div>
              <div className="mt-1 text-2xs text-fg-subtle">0 – 50%. Default 20%.</div>
            </div>
            <Button variant="primary" onClick={save} disabled={busy || loading}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </div>
          {settings && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <MiniInfo label="Monthly" value={`${100 - (settings.yearly_discount_pct || 0)}%`} hint="Customer pays" />
              <MiniInfo label="Yearly" value={`${100 - (settings.yearly_discount_pct || 0) * 0.12}% of 12×`} hint="Effective annual" />
              <MiniInfo label="Saved" value={`${settings.yearly_discount_pct * 0.12}/mo × 12`} hint="Per plan year" />
              <MiniInfo label="Source" value={settings.source === 'db' ? 'Database' : 'Default'} hint="Where the value comes from" />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Read-only system info */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-fg-muted" />
            <div>
              <Card.Title>System info</Card.Title>
              <Card.Subtitle>Read-only. These come from env / config and can't be changed at runtime.</Card.Subtitle>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="p-8 flex items-center justify-center"><Spinner size={20} /></div>
          ) : settings ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <InfoRow icon={Clock}   label="TLS timeout"            value={`${settings.tls_timeout_seconds}s`} />
              <InfoRow icon={Server}  label="Datames max timeout"      value={`${settings.argus_max_timeout_ms}ms`} />
              <InfoRow icon={Cpu}     label="TLS identities"         value={
                <span className="flex flex-wrap gap-1">
                  {settings.client_identifiers.map((c) => (
                    <Badge key={c} variant="default" className="text-2xs font-mono">{c}</Badge>
                  ))}
                </span>
              } />
              <InfoRow icon={Globe}   label="User agents configured" value={settings.user_agents_count} />
              <InfoRow icon={Database} label="Settings source"       value={settings.source === 'db' ? 'database (overridden)' : 'default'} />
              <InfoRow icon={Shield}   label="Settings key"          value="yearly_discount_bps" />
            </div>
          ) : null}
        </Card.Body>
      </Card>

      {/* Placeholder cards for future tunables */}
      <Card>
        <Card.Body className="px-5 py-4 text-sm text-fg-muted flex items-start gap-3">
          <Info className="h-4 w-4 text-fg-subtle mt-0.5 shrink-0" />
          <div>
            <strong className="text-fg">More settings coming.</strong> We'll surface operator-tunable knobs here as we add them — e.g. Datames solve timeout, free-tier rate limits, monthly-refill adjustments, and per-plan visible/hidden flags.
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

function MiniInfo({ label, value, hint }) {
  return (
    <div className="rounded-lg bg-surface-2/50 border border-border p-3">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="text-sm font-medium text-fg mt-0.5 tabular-nums">{value}</div>
      {hint && <div className="text-2xs text-fg-muted mt-0.5">{hint}</div>}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border/40 last:border-b-0">
      <div className="flex items-center gap-2 text-2xs uppercase tracking-wider text-fg-muted font-medium shrink-0">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-sm text-fg text-right">{value}</div>
    </div>
  );
}